import {
  ForwardRefRenderFunction,
  Fragment,
  FunctionComponent,
  ReactNode,
  forwardRef,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import cn from 'classnames';
import scrollIntoView from 'scroll-into-view-if-needed';
import {
  DraftConfigurationWithTextOrSpecialText,
  StoreFontFamily,
  StoreSpecialFont,
} from '@/types';
import { trackFeature } from '@/utils/adobeLaunch';
import { t } from '@/utils/i18n';
import {
  getFontPreviewBackgroundPosition,
  getFontPreviewUrl,
} from '@/utils/imageUrls/getFontPreviewUrl';
import { isCYOMode, isEmbroideryMode } from '@/utils/params';
import { useIsSmall } from '@/hooks/useMediaQuery';
import { useScrollIntoView } from '@/hooks/useScrollIntoView';
import { useThunkActionWithLoadingState } from '@/hooks/useThunkActionWithLoadingState';
import {
  isDraftConfigurationWithText,
  isDraftSpecialTextConfiguration,
} from '@/model/configuration/isDraftConfigurationType';
import { convertSpecialTextToTextConfigurationAction } from '@/redux/configurations/actions/convertSpecialTextToTextConfigurationAction';
import { convertTextToSpecialTextConfigurationAction } from '@/redux/configurations/actions/convertTextToSpecialTextConfigurationAction';
import { getFonts } from '@/redux/core/getters/getFonts';
import { getSpecialFontCommission } from '@/redux/core/getters/getSpecialFontCommission';
import { getSpecialFontsForCurrentConfiguration } from '@/redux/core/getters/getSpecialFontsForCurrentConfiguration';
import {
  setConfigurationSpecialTextStyle,
  setConfigurationTextStyle,
} from '@/redux/draft/actions/setConfigurationTextStyle';
import { Badge, BadgeTypes } from './Badge';
import { LoadingOverlay } from './LoadingOverlay';
import { Price } from './Price';
import { Info } from './icons/Info';
import { Stars } from './icons/Stars';
import './FontList.scss';

interface PreviewProps {
  selected: boolean;
  onClick: () => void;
  style?: object;
  label?: string;
  children?: ReactNode;
}

const FontPreviewWithRef: ForwardRefRenderFunction<HTMLButtonElement, PreviewProps> = (
  { selected, onClick, style, label, children },
  ref
) => (
  <button
    className={cn('font-preview__font', { 'font-preview__font--selected': selected })}
    style={style}
    type="button"
    onClick={onClick}
    ref={ref}
    aria-label={label}
    role="option"
    aria-selected={selected}
  >
    {children}
  </button>
);

const FontPreview = forwardRef<HTMLButtonElement, PreviewProps>(FontPreviewWithRef);

interface FontListProps {
  configuration: DraftConfigurationWithTextOrSpecialText;
}

export const FontList: FunctionComponent<FontListProps> = ({ configuration }) => {
  const fonts = useSelector(getFonts);
  const fontPreview = useMemo(() => getFontPreviewUrl(fonts.map(({ id }) => id)), [fonts]);
  const specialFonts = useSelector(getSpecialFontsForCurrentConfiguration);
  const activeElement = useScrollIntoView<HTMLButtonElement>();
  const [isLoading, setIsLoading] = useState(false);

  const wrappedTextStyleAction = useThunkActionWithLoadingState(
    setConfigurationTextStyle,
    setIsLoading
  );
  const wrappedSpecialTextStyleAction = useThunkActionWithLoadingState(
    setConfigurationSpecialTextStyle,
    setIsLoading
  );
  const wrappedConvertSpecialTextToTextAction = useThunkActionWithLoadingState(
    convertSpecialTextToTextConfigurationAction,
    setIsLoading
  );
  const wrappedConvertTextToSpecialTextAction = useThunkActionWithLoadingState(
    convertTextToSpecialTextConfigurationAction,
    setIsLoading
  );

  const handleFontSelect = (family: StoreFontFamily) => {
    trackFeature('text-font');
    setIsLoading(true);
    if (isDraftConfigurationWithText(configuration)) {
      const style = configuration.text.style;
      const currentFont = style.font;
      const font =
        family.fonts.find(
          (f) => f.style === currentFont.style && f.weight === currentFont.weight
        ) ||
        family.fonts.find((f) => f.style === currentFont.style) ||
        family.fonts.find((f) => f.weight === currentFont.weight) ||
        family.fonts[0];

      wrappedTextStyleAction(configuration, { ...style, font });
    } else {
      wrappedConvertSpecialTextToTextAction(configuration, family);
    }
  };

  const handleSpecialFontSelect = (font: StoreSpecialFont) => {
    setIsLoading(true);
    if (isDraftSpecialTextConfiguration(configuration)) {
      const style = configuration.text.style;
      wrappedSpecialTextStyleAction(configuration, {
        ...style,
        font,
      });
    } else {
      wrappedConvertTextToSpecialTextAction(configuration, font);
    }
  };

  const fontFamilyId = isDraftConfigurationWithText(configuration)
    ? configuration.text.style.font.familyId
    : '';
  const specialFontId = isDraftSpecialTextConfiguration(configuration)
    ? configuration.text.style.font.id
    : '';

  const specialFontRef = useRef<null | HTMLDivElement>(null);
  const handleScroll = () => {
    requestAnimationFrame(() => {
      if (specialFontRef.current) {
        scrollIntoView(specialFontRef.current, {
          scrollMode: 'if-needed',
          block: 'start',
          inline: 'start',
          behavior: 'smooth',
        });
      }
    });
  };
  const [showTooltip, setShowTooltip] = useState(false);
  const isSmall = useIsSmall();
  const cyoSpecialFontCommission = useSelector(getSpecialFontCommission);

  return (
    <Fragment>
      <div className="font-list" data-testid="font-list" role="listbox">
        {isCYOMode() &&
        !isEmbroideryMode() &&
        specialFonts.length > 0 &&
        cyoSpecialFontCommission ? (
          <Fragment>
            <div className="jump-link__wrapper">
              <button className="jump-link" onClick={handleScroll}>
                <Stars height="1.75rem" width="1.75rem" />
                {t('fontList.jumpToSpecial')}
              </button>
            </div>
            <div className="font-section">
              <span className="font-section__label">{t('fontList.regular')}</span>
              {cyoSpecialFontCommission && (
                <Badge type={BadgeTypes.Organic}>{t('designSelector.free')}</Badge>
              )}
            </div>
          </Fragment>
        ) : null}
        {fonts.map((family) => {
          const selected = family.id === fontFamilyId;
          return (
            <FontPreview
              key={family.id}
              selected={selected}
              onClick={() => handleFontSelect(family)}
              style={{
                backgroundImage: `url(${fontPreview})`,
                backgroundPosition: `center ${getFontPreviewBackgroundPosition(
                  fonts.length,
                  family.index
                )}`,
              }}
              label={family.name}
              ref={selected ? activeElement : null}
            />
          );
        })}
        {isCYOMode() &&
        !isEmbroideryMode() &&
        specialFonts.length > 0 &&
        cyoSpecialFontCommission ? (
          <div className="font-section" ref={specialFontRef}>
            <span className="font-section__label">{t('fontList.special')}</span>
            <span className="font-section__price-wrapper">
              {isSmall ? (
                <button
                  className="font-section__tooltip-container"
                  onClick={() => setShowTooltip(!showTooltip)}
                >
                  <Info className="font-section__info" />
                  +<Price value={cyoSpecialFontCommission} />
                  {showTooltip && (
                    <div className="font-section__tooltip">
                      <div className="font-section__tooltip__arrow" />
                      <span>{t('fontList.surchargeTooltip')}</span>
                    </div>
                  )}
                </button>
              ) : (
                <div className="font-section__tooltip-container">
                  <Info className="font-section__info" />
                  +<Price value={cyoSpecialFontCommission} />
                  <div className="font-section__tooltip">
                    <div className="font-section__tooltip__arrow" />
                    <span>{t('fontList.surchargeTooltip')}</span>
                  </div>
                </div>
              )}
            </span>
          </div>
        ) : null}
        {specialFonts.map((font) => {
          const selected = font.id === specialFontId;
          return (
            <FontPreview
              key={font.id}
              selected={selected}
              onClick={() => handleSpecialFontSelect(font)}
              ref={selected ? activeElement : null}
            >
              <img className="font-preview__img" src={font.icon} alt="" />
              {font.title}
            </FontPreview>
          );
        })}
      </div>
      {isLoading && <LoadingOverlay />}
    </Fragment>
  );
};
